// High Score Manager - Handles both local and API high scores

import { fetchHighScores, submitHighScore, type HighScore } from '../api/highscores';

const STORAGE_KEY = 'pacman-high-score';
const GAME_ID = 'pacman';
const DISPLAY_COUNT = 10;
const CACHE_TIMEOUT = 30000; // 30 seconds

export class HighScoreManager {
  private localHighScore: number;
  private scores: HighScore[] = [];
  private error: string | null = null;
  private cacheTime: number = 0;
  private submittedScoreId: string | null = null;
  private loading: boolean = false;

  constructor() {
    this.localHighScore = this.loadLocal();
  }

  // ============================================
  // Local Storage (fallback)
  // ============================================

  private loadLocal(): number {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const value = parseInt(stored, 10);
        return isNaN(value) ? 0 : value;
      }
    } catch {
      // localStorage may be unavailable
    }
    return 0;
  }

  private saveLocal(): void {
    try {
      localStorage.setItem(STORAGE_KEY, this.localHighScore.toString());
    } catch {
      // localStorage may be unavailable
    }
  }

  getLocalHighScore(): number {
    return this.localHighScore;
  }

  updateLocalHighScore(score: number): boolean {
    if (score > this.localHighScore) {
      this.localHighScore = score;
      this.saveLocal();
      return true;
    }
    return false;
  }

  // ============================================
  // API High Scores
  // ============================================

  /**
   * Load high scores from the API
   */
  async loadScores(): Promise<void> {
    if (this.loading) return;

    this.loading = true;
    try {
      this.scores = await fetchHighScores(GAME_ID, DISPLAY_COUNT);
      this.error = null;
      this.cacheTime = Date.now();
    } catch (error) {
      console.error('Failed to load high scores:', error);
      this.error = 'UNABLE TO LOAD SCORES';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Submit a new high score
   */
  async submitScore(playerName: string, score: number, level: number): Promise<boolean> {
    try {
      const response = await submitHighScore({
        gameId: GAME_ID,
        playerName,
        score,
        level,
      });

      if (response.success) {
        this.submittedScoreId = response.id ?? null;
        console.log(`Score submitted! Rank: ${response.rank}`);
        return true;
      } else {
        console.error('Score submission failed:', response.error);
        return false;
      }
    } catch (error) {
      console.error('Failed to submit score:', error);
      return false;
    }
  }

  /**
   * Check if a score qualifies for the high score list
   */
  isHighScore(score: number): boolean {
    // Always allow if no scores loaded yet or list not full
    if (this.scores.length === 0 || this.scores.length < DISPLAY_COUNT) {
      return score > 0;
    }

    // Check if score beats the lowest score
    const lowestScore = this.scores[this.scores.length - 1]?.score ?? 0;
    return score > lowestScore;
  }

  /**
   * Check if cache is expired
   */
  isCacheExpired(): boolean {
    return Date.now() - this.cacheTime > CACHE_TIMEOUT;
  }

  /**
   * Refresh scores if cache expired
   */
  async refreshIfNeeded(): Promise<void> {
    if (this.isCacheExpired()) {
      await this.loadScores();
    }
  }

  /**
   * Get current scores
   */
  getScores(): HighScore[] {
    return this.scores;
  }

  /**
   * Get the highest score from the leaderboard or local
   */
  getHighScore(): number {
    const apiHighScore = this.scores[0]?.score ?? 0;
    return Math.max(this.localHighScore, apiHighScore);
  }

  /**
   * Get error message if any
   */
  getError(): string | null {
    return this.error;
  }

  /**
   * Check if currently loading
   */
  isLoading(): boolean {
    return this.loading;
  }

  /**
   * Get ID of recently submitted score
   */
  getSubmittedScoreId(): string | null {
    return this.submittedScoreId;
  }

  /**
   * Clear submitted score ID (e.g., when returning to menu)
   */
  clearSubmittedScoreId(): void {
    this.submittedScoreId = null;
  }
}
